/** Orders domain events — catalog 1.5.0 additions. */
import {
  asEventRecord,
  defineEvent,
  requireString,
  type EventDefinition,
  type EventSchema,
} from './registry';

export interface OrderCreatedPayload {
  orgId: string;
  orderId: string;
}

export interface OrderQuotedPayload extends OrderCreatedPayload {
  quoteId: string;
}

export type OrderClosedPayload = OrderCreatedPayload;

export type OrderCancelledPayload = OrderCreatedPayload;

export interface OrderRefundPayload extends OrderCreatedPayload {
  originalOrderId: string;
}

export interface OrderExchangeCreatedPayload extends OrderCreatedPayload {
  originalOrderId: string;
}

export type OrderCloseCompensationSeverity = 'info' | 'warning' | 'error' | 'critical';

export interface OrderCloseCompensatedPayload extends OrderCreatedPayload {
  severity: OrderCloseCompensationSeverity;
}

const stringFieldsSchema = <T extends object>(
  type: string,
  fields: readonly string[],
): EventSchema<T> => ({
  parse(data) {
    const record = asEventRecord(data, type);
    const parsed: Record<string, unknown> = {};
    for (const field of fields) {
      parsed[field] = requireString(record[field], field, type);
    }
    return parsed as T;
  },
});

const compensationSchema: EventSchema<OrderCloseCompensatedPayload> = {
  parse(data) {
    const record = asEventRecord(data, 'orders:close-compensated');
    const severity = record.severity;
    if (
      severity !== 'info' &&
      severity !== 'warning' &&
      severity !== 'error' &&
      severity !== 'critical'
    ) {
      throw new Error(
        'orders:close-compensated: field "severity" must be info, warning, error, or critical',
      );
    }
    return {
      orgId: requireString(record.orgId, 'orgId', 'orders:close-compensated'),
      orderId: requireString(record.orderId, 'orderId', 'orders:close-compensated'),
      severity,
    };
  },
};

const defineOrderEvent = <T extends object>(
  type: string,
  description: string,
  schema: EventSchema<T>,
): EventDefinition<T> =>
  defineEvent<T>({
    type,
    version: 1,
    schema,
    auditable: true,
    description,
    since: '1.5.0',
  });

export const ORDERS_ORDER_CREATED_EVENT = defineOrderEvent(
  'orders:order-created',
  'An order was created.',
  stringFieldsSchema<OrderCreatedPayload>('orders:order-created', ['orgId', 'orderId']),
);
export const ORDERS_ORDER_QUOTED_EVENT = defineOrderEvent(
  'orders:order-quoted',
  'An order was quoted.',
  stringFieldsSchema<OrderQuotedPayload>('orders:order-quoted', ['orgId', 'orderId', 'quoteId']),
);
export const ORDERS_ORDER_CLOSED_EVENT = defineOrderEvent(
  'orders:order-closed',
  'An order was closed.',
  stringFieldsSchema<OrderClosedPayload>('orders:order-closed', ['orgId', 'orderId']),
);
export const ORDERS_ORDER_CANCELLED_EVENT = defineOrderEvent(
  'orders:order-cancelled',
  'An order was cancelled.',
  stringFieldsSchema<OrderCancelledPayload>('orders:order-cancelled', ['orgId', 'orderId']),
);
export const ORDERS_REFUND_ISSUED_EVENT = defineOrderEvent(
  'orders:refund-issued',
  'A refund was issued for an order.',
  stringFieldsSchema<OrderRefundPayload>('orders:refund-issued', [
    'orgId',
    'orderId',
    'originalOrderId',
  ]),
);
export const ORDERS_REFUND_PENDING_EVENT = defineOrderEvent(
  'orders:refund-pending',
  'A refund is pending settlement.',
  stringFieldsSchema<OrderRefundPayload>('orders:refund-pending', [
    'orgId',
    'orderId',
    'originalOrderId',
  ]),
);
export const ORDERS_EXCHANGE_CREATED_EVENT = defineOrderEvent(
  'orders:exchange-created',
  'An exchange order was created.',
  stringFieldsSchema<OrderExchangeCreatedPayload>('orders:exchange-created', [
    'orgId',
    'orderId',
    'originalOrderId',
  ]),
);
export const ORDERS_CLOSE_COMPENSATED_EVENT = defineOrderEvent(
  'orders:close-compensated',
  'An order close was compensated after a failed commit.',
  compensationSchema,
);

export const ORDERS_EVENTS: Readonly<Record<string, EventDefinition>> = {
  [ORDERS_ORDER_CREATED_EVENT.type]: ORDERS_ORDER_CREATED_EVENT,
  [ORDERS_ORDER_QUOTED_EVENT.type]: ORDERS_ORDER_QUOTED_EVENT,
  [ORDERS_ORDER_CLOSED_EVENT.type]: ORDERS_ORDER_CLOSED_EVENT,
  [ORDERS_ORDER_CANCELLED_EVENT.type]: ORDERS_ORDER_CANCELLED_EVENT,
  [ORDERS_REFUND_ISSUED_EVENT.type]: ORDERS_REFUND_ISSUED_EVENT,
  [ORDERS_REFUND_PENDING_EVENT.type]: ORDERS_REFUND_PENDING_EVENT,
  [ORDERS_EXCHANGE_CREATED_EVENT.type]: ORDERS_EXCHANGE_CREATED_EVENT,
  [ORDERS_CLOSE_COMPENSATED_EVENT.type]: ORDERS_CLOSE_COMPENSATED_EVENT,
};
